import { MouseEvent } from 'react'
import { IconButton, Menu, MenuItem } from '@mui/material'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import { PanelRightOpen } from 'lucide-react'

interface RowActionsButtonProps {
  /** True while this row's menu is open, which keeps the button visible. */
  active: boolean
  onClick: (anchor: HTMLElement) => void
}

/**
 * The `⋯` button rendered in every row. Hidden until the row is hovered (see the
 * `.row-actions-button` rule in InsightsTable) or the button itself is focused.
 */
export const RowActionsButton = ({ active, onClick }: RowActionsButtonProps) => {
  const handleClick = (e: MouseEvent<HTMLElement>) => {
    // The row's own click handler opens the detail drawer; this click must not reach it.
    e.stopPropagation()
    onClick(e.currentTarget)
  }

  return (
    <IconButton
      aria-label="Row actions"
      aria-haspopup="menu"
      aria-expanded={active}
      onClick={handleClick}
      className="row-actions-button"
      sx={{
        width: 28,
        height: 28,
        borderRadius: '4px',
        opacity: active ? 1 : 0,
        pointerEvents: active ? 'auto' : 'none',
        transition: 'opacity .12s',
        bgcolor: active ? 'rgba(4,84,174,0.16)' : 'rgba(4,84,174,0.1)',
        '&:hover': { bgcolor: 'rgba(4,84,174,0.16)' },
        '&:focus-visible': { opacity: 1, pointerEvents: 'auto' },
      }}
    >
      <MoreHorizIcon sx={{ width: 18, height: 18, color: '#0454AE' }} />
    </IconButton>
  )
}

interface RowActionsMenuProps {
  anchorEl: HTMLElement | null
  onClose: () => void
  onExploreInsight: () => void
  onDownloadInsightImage: () => void
}

const menuItemSx = { fontSize: '0.875rem', color: '#051E33', gap: '12px', py: 1, px: 2 }

/**
 * The menu the `⋯` button opens. A single instance is rendered by the table and
 * re-anchored to whichever row's button was clicked, rather than one menu per row.
 */
export const RowActionsMenu = ({ anchorEl, onClose, onExploreInsight, onDownloadInsightImage }: RowActionsMenuProps) => (
  <Menu
    anchorEl={anchorEl}
    open={anchorEl !== null}
    onClose={onClose}
    onClick={(e) => e.stopPropagation()}
    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    slotProps={{ paper: { sx: { minWidth: 200, borderRadius: '4px' } } }}
  >
    <MenuItem
      onClick={() => {
        onClose()
        onExploreInsight()
      }}
      sx={menuItemSx}
    >
      <PanelRightOpen size={18} />
      Explore insight
    </MenuItem>
    <MenuItem
      onClick={() => {
        onClose()
        onDownloadInsightImage()
      }}
      sx={menuItemSx}
    >
      <FileDownloadOutlinedIcon sx={{ width: 18, height: 18 }} />
      Share insight / Download PNG
    </MenuItem>
  </Menu>
)
